import { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Clock, CheckCircle2, TruckIcon, TrendingUp, Plus } from "lucide-react";
import { fmtBRL } from "../lib/constants";

const WEEK_LABELS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

function KPI({ icon: Icon, label, value, color, testId }) {
  return (
    <div
      data-testid={testId}
      className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-all hover:-translate-y-[1px]"
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</div>
          <div className="mt-2 font-heading text-3xl font-bold text-slate-900">{value}</div>
        </div>
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center"
          style={{ background: color + "18", color }}
        >
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard({ stats, orders, onNewOrder, currentMonth }) {
  // Compute revenue per weekday (delivered orders)
  const weekData = useMemo(() => {
    const map = Object.fromEntries(WEEK_LABELS.map((d) => [d, 0]));
    orders
      .filter((o) => o.status === "delivered" && o.exitDate)
      .forEach((o) => {
        const [dd, mm, yy] = o.exitDate.split("/");
        const dt = new Date(`${yy}-${mm}-${dd}`);
        if (!isNaN(dt.getTime())) {
          map[WEEK_LABELS[dt.getDay()]] += Number(o.total) || 0;
        }
      });
    return WEEK_LABELS.map((day) => ({ day, value: map[day] }));
  }, [orders]);

  const recentDelivered = orders.filter((o) => o.status === "delivered").slice(0, 5);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="dashboard-view">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Painel</h2>
          <p className="text-sm text-slate-500 mt-0.5">Visão geral de {currentMonth.toLowerCase()}</p>
        </div>
        <button
          onClick={onNewOrder}
          data-testid="dashboard-new-os-button"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-all hover:-translate-y-[1px] hover:shadow-lg"
        >
          <Plus className="w-4 h-4" /> Nova OS
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <KPI icon={Clock} label="Recebidos" value={stats.received} color="#2563eb" testId="kpi-received" />
        <KPI icon={CheckCircle2} label="Prontos" value={stats.ready} color="#059669" testId="kpi-ready" />
        <KPI icon={TruckIcon} label="Entregues" value={stats.delivered} color="#64748b" testId="kpi-delivered" />
        <KPI
          icon={TrendingUp}
          label="Receita"
          value={`R$ ${fmtBRL(stats.revenue)}`}
          color="#7c3aed"
          testId="kpi-revenue"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-slate-900">Receita da semana</h3>
            <span className="text-xs text-slate-400 uppercase tracking-wider font-semibold">R$</span>
          </div>
          <div style={{ width: "100%", height: 240, minHeight: 240 }}>
            <ResponsiveContainer>
              <BarChart data={weekData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <CartesianGrid stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="day" tick={{ fill: "#64748b", fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip
                  cursor={{ fill: "rgba(37,99,235,0.06)" }}
                  contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }}
                  formatter={(v) => [`R$ ${fmtBRL(v)}`, "Receita"]}
                />
                <Bar dataKey="value" fill="#2563eb" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <h3 className="font-heading font-semibold text-slate-900 mb-3">Entregas recentes</h3>
          {recentDelivered.length === 0 ? (
            <p className="text-sm text-slate-400 italic py-6 text-center">Nenhuma entrega ainda.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {recentDelivered.map((o) => (
                <li key={o.id} className="py-2.5 flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{o.clientName}</div>
                    <div className="text-[11px] text-slate-400">
                      {o.model} · {o.exitDate}
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-slate-700 tabular-nums">R$ {fmtBRL(o.total)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
